import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { GridSortItem } from '@mui/x-data-grid/models/gridSortModel';
import {
  DataGridProProps,
  GRID_TREE_DATA_GROUPING_FIELD,
  GridDataSource,
  GridRowSelectionModel,
} from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { isArray } from 'lodash-es';
import { useLocation } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

import ContractingPartiesList from './ContractingPartiesList';
import CustomGridTreeDataGroupingCell from './GroupCell';
import TableTooltip from './TableTooltip';
import { useUserData } from '../../../App/Component/UserDataProvider';
import useSessionStorage from '../../../App/Component/useSessionStorage';
import { WEBSOCKET_API } from '../../../Configuration/global';
import { DRAG_MIME } from '../../../DocumentFoldersView/FoldersUtil';
import { sortListAlphabetical } from '../../../Draft/Component/Helper';
import { IObject } from '../../../interfaces/IObject';
import OverflowTip from '../../../RiverusUI/Components/OverflowTip';
import DataGridProTable from '../../../RiverusUI/DataGrid/DataGridProTable';
import NameAvatar from '../../../RiverusUI/DataGrid/NameAvatar';
import TableChipList from '../../../RiverusUI/DataGrid/TableChipList';
import TableHeaderWithSearch from '../../../RiverusUI/DataGrid/TableHeaderWIthSearch';
import TableSearchInputComponent from '../../../RiverusUI/DataGrid/TableSearchInputComponent';
import TableSearchSelectInput from '../../../RiverusUI/DataGrid/TableSearchSelectInput';
import { getUsers } from '../../../Services/Approval';
import {
  fetchGroups,
  getAllConnectedDocHierarchy,
} from '../../../Services/DocumentLibrary';
import { getTableColumn } from '../../../Services/setting';
import { useDocumentLibrary } from '../DocumentLibraryProvider';
import {
  cellStyle,
  findByIdInChildrenOnly,
  getProgressStatus,
  getTooltipTitle,
} from './helper';
import {
  sourceTypes,
  sourceTypesArray,
} from '../../../Draft/Component/CreateDraft/staticData';
import { useResponsive } from '../../../RiverusUI/Components/ResponsiveProvider';
import { GridCellExpand } from '../../../RiverusUI/DataGrid/GridCellExpand';
import { renewAmendChipValue } from '../DocLibStaticData';

interface Props {
  tableData: any;
  setSelectedRow: Dispatch<SetStateAction<GridRowSelectionModel>>;
  setPageNumberChange: Dispatch<SetStateAction<number>>;
  setFilters: Dispatch<SetStateAction<IObject>>;
  setSorting?: Dispatch<SetStateAction<GridSortItem[]>>;
  filters?: IObject;
  isLoading: boolean;
  pageNumber: number;
  refreshKey?: boolean;
  selectedRowData?: any;
  onStatusChange?: any;
  treeView?: boolean;
  folderView?: boolean;
  titleSearchValue: any;
  setTitleSearchValue: Dispatch<SetStateAction<any>>;
  refIdSearchValue: any;
  setRefIdSearchValue: Dispatch<SetStateAction<any>>;
  partySearchValue: any;
  setPartySearchValue: Dispatch<SetStateAction<any>>;
  contracts: any;
  contractCategory: any;
  projectData: any;
  productMasterData: any;
  territoryMasterData: any;
  partyMasterData?: any;
}

const DocumentLibraryTable: React.FC<Props> = ({
  tableData,
  setSelectedRow,
  setPageNumberChange,
  setFilters,
  filters,
  isLoading,
  pageNumber,
  setSorting,
  refreshKey,
  selectedRowData,
  onStatusChange,
  treeView = false,
  folderView = false,
  titleSearchValue,
  setTitleSearchValue,
  refIdSearchValue,
  setRefIdSearchValue,
  partySearchValue,
  setPartySearchValue,
  contracts,
  contractCategory,
  projectData,
  productMasterData,
  territoryMasterData,
  partyMasterData,
}) => {
  const { isMobile, isTablet } = useResponsive();
  const { activeHeader, setFilterNames } = useDocumentLibrary();
  const [searchOwner, setSearchOwner] = useState<boolean>(false);
  const [searchContract, setSearchContract] = useState<boolean>(false);
  const [searchSource, setSearchSource] = useState<boolean>(false);
  const [searchContractType, setSearchContractType] = useState<boolean>(false);
  const [searchGroups, setSearchGroups] = useState<boolean>(false);
  const [searchProjects, setSearchProjects] = useState<boolean>(false);
  const [searchContractingParties, setSearchContractingParties] =
    useState<boolean>(false);
  const [searchProductMaster, setSearchProductMaster] =
    useState<boolean>(false);
  // const [searchStatus, setSearchStatus] = useState<boolean>(false);
  const [searchTerritory, setSearchTerritory] = useState<boolean>(false);
  const [contractStatus, setContractStatus] = useState<any>([]);
  const [contractData, setContractData] = useState<any>(null);
  const [searchCategory, setSearchCategory] = useState<boolean>(false);
  const [searchReferenceId, setSearchReferenceId] = useState<boolean>(false);
  const [projectLabel] = useSessionStorage<any>('project_label');
  const [territory] = useSessionStorage<any>('territory_config');

  const { data: contractColumnData } = useQuery({
    queryKey: ['contract-table-column'],
    queryFn: getTableColumn,
    select: (response: any) => response?.results,
    staleTime: 5 * 60 * 1000,
  });

  const { data: groupData } = useQuery({
    queryKey: ['groups'],
    queryFn: fetchGroups,
    staleTime: 5 * 60 * 1000,
  });

  const { data: ownerData } = useQuery({
    queryKey: ['owner'],
    queryFn: getUsers,
    select: (response: any) => {
      const users = response?.users || [];
      for (let i = 0; i < users.length; i++) {
        const name = users[i].first_name + ' ' + users[i].last_name;
        users[i].name = name;
      }
      return users;
    },
    staleTime: 5 * 60 * 1000,
  });

  const contractColumns = useMemo(() => {
    if (contractColumnData) {
      const contractColumn = contractColumnData?.find(
        (item: any) => item.table_name === 'Contract'
      );
      return contractColumn?.columns || [];
    }
    return [];
  }, [contractColumnData]);

  const handleOpenContract = (contractData: any) => {
    window.open(
      '/document/documentlibrary/' + btoa(contractData?.id?.toString()),
      '_blank'
    );
  };

  const { user_id } = useUserData();
  const canDrag = folderView && !treeView;

  const handleContractDragStart = (e: React.DragEvent, row: any) => {
    const selected = (selectedRowData || []).map((r: any) => String(r.id));
    const ids = selected.includes(String(row.id)) ? selected : [String(row.id)];
    e.dataTransfer.setData(DRAG_MIME, JSON.stringify(ids));
    e.dataTransfer.effectAllowed = 'move';
    const ghost = document.createElement('div');
    ghost.textContent = `Moving ${ids.length} contract${ids.length > 1 ? 's' : ''}`;
    ghost.style.cssText =
      'position:fixed;top:-100px;left:-100px;padding:8px 14px;background:#410224;color:#fff;border-radius:8px;font:14px Roboto,sans-serif';
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 10, 10);
    setTimeout(() => ghost.remove(), 0);
  };

  useEffect(() => {
    if (tableData) {
      setContractData(tableData);
    }
  }, [tableData]);

  useEffect(() => {
    setTimeout(() => {
      if (user_id) {
        let ws = new WebSocket(
          WEBSOCKET_API + `ws/kdp_status/?token=${user_id}`
        );

        ws.onclose = () => {
          ws = new WebSocket(WEBSOCKET_API + `ws/kdp_status/?token=${user_id}`);
        };

        ws.onmessage = (event) => {
          const contract_status: any = JSON.parse(event.data)['message'];
          setContractStatus(contract_status);
        };

        // Cleanup the WebSocket connection on component unmount
        return () => {
          ws.close();
        };
      }
    }, 6000);
  }, [user_id]);

  // function CircularProgressWithLabel(
  //   props: CircularProgressProps & { value: number }
  // ) {
  //   return (
  //     <Box sx={{ position: 'relative', display: 'inline-flex' }}>
  //       <CircularProgress variant="determinate" {...props} />
  //       <Box
  //         sx={{
  //           top: 0,
  //           left: 0,
  //           bottom: 0,
  //           right: 0,
  //           position: 'absolute',
  //           display: 'flex',
  //           alignItems: 'center',
  //           justifyContent: 'center',
  //         }}
  //       >
  //         <Typography
  //           variant="caption"
  //           component="div"
  //           color="text.secondary"
  //         >{`${Math.round(props.value)}%`}</Typography>
  //       </Box>
  //     </Box>
  //   );
  // }

  const progressStatus = useCallback(
    (id: string) => {
      return contractStatus?.find((item: any) => item?.id === id);
    },
    [contractStatus]
  );

  const { search } = useLocation();
  const queryParams = React.useMemo(
    () => new URLSearchParams(search),
    [search]
  );

  const getType = queryParams.get('type');
  const getName = queryParams.get('name');
  const getId = queryParams.get('id');

  const getFilterNames = (
    label: string,
    value: string | string[] | null
  ): string[] | string | null => {
    if (!value || (Array.isArray(value) && value.length === 0)) return null;

    // centralized mapping for label -> dataset + keys
    const sourceMap: Record<
      string,
      { data?: any[]; idKey?: string; nameKey?: string }
    > = {
      project: { data: projectData },
      productmasters: { data: productMasterData },
      territory: { data: territoryMasterData },
      contracttype: { data: contracts },
      contractcategory: { data: contractCategory },
      source_type: { data: sourceTypesArray, idKey: 'value', nameKey: 'label' },
      creator: { data: ownerData },
    };

    const source = sourceMap[label];
    if (!source?.data) return value;

    const { data, idKey = 'id', nameKey = 'name' } = source;

    const filtered = data
      ?.filter((item) => (value as string[])?.includes(item[idKey]))
      ?.map((item) => item[nameKey]);

    return filtered?.length ? filtered : value;
  };

  const handleFilterChange = useDebouncedCallback(
    (label: string, value: string | string[], name: string) => {
      if (value?.length) {
        setPageNumberChange(0);
      }
      setFilters((prev: IObject) => ({ ...prev, [label]: value }));
      setFilterNames((prev: IObject) => ({
        ...prev,
        [name]: getFilterNames(label, value),
      }));
    },
    1000
  );

  const handleFilterClick = useCallback(
    (
      key: string,
      label: string,
      value: any,
      searchSetter?: React.Dispatch<React.SetStateAction<boolean>>
    ) => {
      const selectedIds = Array.isArray(value)
        ? value.map((v) => v?.id ?? v)
        : [value?.id ?? value];

      setFilters((prev: IObject) => ({
        ...prev,
        [key]: selectedIds,
      }));

      setFilterNames((prev: IObject) => ({
        ...prev,
        [label]: getFilterNames(key, selectedIds),
      }));

      if (selectedIds.length) setPageNumberChange(0);
      searchSetter?.(true);
    },
    [getFilterNames]
  );

  useEffect(() => {
    if (!filters?.title) {
      setSearchContract(false);
    }
    if (!filters?.groups) {
      setSearchGroups(false);
    }
    // if (!filters?.contracting_parties && !partyMasterData?.length) {
    //   setSearchContractingParties(false);
    // }
    if (!filters?.contracting_parties) {
      setSearchContractingParties(false);
    }
    if (!filters?.contractcategory) {
      setSearchCategory(false);
    }
    if (!filters?.reference_id) {
      setSearchReferenceId(false);
    }
    // if (!filters?.status) {
    //   setSearchStatus(false);
    // }
  }, [filters, refreshKey]);

  useEffect(() => {
    if (!filters?.contracttype) {
      setSearchContractType(false);
    }
    if (!filters?.project) {
      setSearchProjects(false);
    }
    if (!filters?.productmasters) {
      setSearchProductMaster(false);
    }
    if (!filters?.territory) {
      setSearchTerritory(false);
    }
    if (!filters?.source_type) {
      setSearchSource(false);
    }
    // if (!filters?.contracting_parties && partyMasterData?.length) {
    //   setSearchContractingParties(false);
    // }
  }, [activeHeader, refreshKey]);

  useEffect(() => {
    if (getType && getName && getId) {
      if (getType !== 'territory') {
        setFilters((prev: IObject) => ({ ...prev, [getType]: [getId] }));
      }
      if (getType === 'contracttype') {
        setSearchContractType(true);
        if (getName) {
          setFilterNames((prev: IObject) => ({
            ...prev,
            'Contract Name': getFilterNames('contract_type', [getName]),
          }));
        }
      } else if (getType === 'groups') {
        setSearchGroups(true);
        if (getName) {
          setFilterNames((prev: IObject) => ({
            ...prev,
            Groups: [getName],
          }));
        }
      } else if (getType === 'project') {
        setSearchProjects(true);
        if (getName) {
          setFilterNames((prev: IObject) => ({
            ...prev,
            Projects: getFilterNames('projects', [getName]),
          }));
        }
      } else if (getType === 'contractcategory') {
        setSearchCategory(true);
        if (getName) {
          setFilterNames((prev: IObject) => ({
            ...prev,
            'Contract category': getFilterNames('contractcategory', [getName]),
          }));
        }
      } else if (getType === 'territory') {
        setSearchTerritory(true);
        if (getName) {
          setFilterNames((prev: IObject) => ({
            ...prev,
            Territory: getFilterNames('territory', [getName]),
          }));
        }
        const territoryId = Number(getId);
        setFilters((prev: any) => ({ ...prev, [getType]: [territoryId] }));
      }
    }
  }, [getType, getName, getId]);

  useEffect(() => {
    let shouldDisable = false;
    if (selectedRowData) {
      for (const row of selectedRowData) {
        const status = getProgressStatus(row, contractStatus);
        if (status.isProcessing || status.notStarted || status.failed) {
          shouldDisable = true;
          break;
        }
      }
    }
    onStatusChange({
      isProcessing: shouldDisable,
      notStarted: shouldDisable,
      failed: shouldDisable,
    });
  }, [selectedRowData, onStatusChange, contractStatus]);

  const customDataSource: GridDataSource = useMemo(
    () => ({
      getRows: async (params) => {
        let newRows: any = [];
        if (newRows.length === 0) {
          newRows = [...(contractData?.results || [])];
        }
        if (params.groupKeys?.length) {
          const index = params.groupKeys?.length - 1;
          const parentData = findByIdInChildrenOnly(
            newRows,
            params.groupKeys?.[index]
          );
          if (
            params.groupKeys.length === 1 &&
            parentData?.children?.length === 0
          ) {
            const hierarchy = await getAllConnectedDocHierarchy(parentData?.id);
            const childData = hierarchy?.results[0];
            const parentIndex = newRows.findIndex(
              (row: any) => row.id === parentData?.id
            );
            newRows[parentIndex].children = childData?.children;
            // Ensure children have path property for tree data
            const childrenWithPath = (childData?.children || []).map(
              (data: any) => ({
                ...data,
                path: [...(params.groupKeys || []), data.id],
                hasChildren: data.child_count > 0,
              })
            );
            return {
              rows: childrenWithPath,
              rowCount: childrenWithPath?.length || 0,
            };
          } else {
            const rows = parentData?.children?.map((data: any) => {
              data.path = [...(params.groupKeys || []), data.id];
              data.hasChildren = data.child_count > 0;
              return data;
            });
            return {
              rows: rows,
              rowCount: rows?.length || 0,
            };
          }
        } else {
          // Ensure root rows have a path property for tree data
          const rootRows = (contractData?.results || []).map((row: any) => ({
            ...row,
            path: row.path || [row.id],
          }));
          return {
            rows: rootRows,
            rowCount: contractData?.count || 0,
          };
        }
      },
      getGroupKey: (row) => row.id,
      getChildrenCount: (row) => row.child_count,
    }),
    [contractData]
  );

  const isShowCategoryColumn = useMemo(() => {
    if (filters?.contractcategory) return true;

    return contractData?.results?.some(
      (item: any) => item?.contract_category?.id
    );
  }, [contractData?.results, filters?.contractcategory]);

  const columns = [
    {
      ...(contractColumns.includes('title') &&
        !treeView && {
          field: 'title',
          headerName: 'Title',
          minWidth: 350,
          flex: 1,
          sortable: !searchContract,
          renderHeader: () => {
            return searchContract ? (
              <TableSearchInputComponent
                key="contract"
                setIsSearch={() => {
                  setTitleSearchValue('');
                  setFilters((prev) => ({ ...prev, title: null }));
                  setSearchContract(false);
                  setFilterNames((prev: IObject) => ({
                    ...prev,
                    Title: null,
                  }));
                }}
                placeholder="Search Name"
                value={titleSearchValue}
                isLoading={isLoading}
                handleChange={(
                  e: React.BaseSyntheticEvent<HTMLInputElement>
                ) => {
                  const val = e.target.value;
                  setTitleSearchValue(val);
                  handleFilterChange('title', val, 'Title');
                }}
              />
            ) : (
              <Stack onClick={(e: any) => e.stopPropagation()}>
                <TableHeaderWithSearch
                  title="Title"
                  setIsSearch={setSearchContract}
                  isLoading={isLoading}
                />
              </Stack>
            );
          },
          renderCell: (params: any) => {
            const { progress, isProcessing, notStarted, failed } =
              getProgressStatus(params?.row, contractStatus);
            return (
              <Tooltip
                title={getTooltipTitle({
                  isProcessing,
                  notStarted,
                  failed,
                  progress,
                })}
                arrow
              >
                <Stack
                  sx={{ minWidth: '220px' }}
                  height="-webkit-fill-available"
                  justifyContent="center"
                  draggable={canDrag}
                  onDragStart={
                    canDrag
                      ? (e: React.DragEvent) =>
                          handleContractDragStart(e, params?.row)
                      : undefined
                  }
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                    sx={{
                      ...cellStyle({ notStarted, isProcessing }),
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      sx={{
                        maxWidth: '220px',
                        display: 'flex',
                        cursor: canDrag ? 'grab' : undefined,
                      }}
                    >
                      {canDrag && (
                        <DragIndicatorIcon
                          fontSize="small"
                          sx={{ color: '#aaa', mr: 0.5 }}
                        />
                      )}
                      <GridCellExpand
                        value={params?.row?.title}
                        width={220}
                        onClick={() => handleOpenContract(params?.row)}
                      />
                    </Stack>
                    <Stack
                      direction="row"
                      alignItems="center"
                      sx={{ ml: 2, minWidth: 60, justifyContent: 'flex-end' }}
                    >
                      {renewAmendChipValue(params?.row?.docID) ? (
                        <Chip
                          label={renewAmendChipValue(params?.row?.docID)}
                          sx={{ background: '#FFECF1', cursor: 'text' }}
                        />
                      ) : (
                        <Box sx={{ width: 40 }} />
                      )}
                      <TableTooltip contractData={params?.row} />
                    </Stack>
                  </Stack>
                </Stack>
              </Tooltip>
            );
          },
        }),
    },
    {
      ...(contractColumns.includes('reference_id') && {
        field: 'reference_id',
        headerName: 'Reference Id',
        minWidth: 240,
        flex: 1,
        sortable: !searchReferenceId,
        renderHeader: () => {
          return searchReferenceId ? (
            <TableSearchInputComponent
              key="reference_id"
              setIsSearch={() => {
                setRefIdSearchValue('');
                setFilters((prev) => ({ ...prev, reference_id: null }));
                setSearchReferenceId(false);
                setFilterNames((prev: IObject) => ({
                  ...prev,
                  'Reference ID': null,
                }));
              }}
              placeholder="Search Reference Id"
              value={refIdSearchValue}
              isLoading={isLoading}
              handleChange={(e: React.BaseSyntheticEvent<HTMLInputElement>) => {
                const val = e.target.value;
                setRefIdSearchValue(val);
                handleFilterChange('reference_id', val, 'Reference ID');
              }}
            />
          ) : (
            <Stack onClick={(e) => e.stopPropagation()}>
              <TableHeaderWithSearch
                title="Reference Id"
                setIsSearch={setSearchReferenceId}
                isLoading={isLoading}
              />
            </Stack>
          );
        },
        renderCell: (params: any) => {
          return (
            <Stack
              sx={{ minWidth: 240 }}
              height="-webkit-fill-available"
              justifyContent="center"
            >
              <Typography>{params?.row?.reference_id}</Typography>
            </Stack>
          );
        },
      }),
    },
    {
      ...(contractColumns.includes('contracting_parties') && {
        field: 'contracting_parties',
        headerName: 'Parties',
        minWidth: 220,
        flex: 1,
        sortable: false,
        renderHeader: () => {
          return searchContractingParties ? (
            <TableSearchInputComponent
              key="contracting_parties"
              setIsSearch={() => {
                setPartySearchValue('');
                setFilters((prev) => ({ ...prev, contracting_parties: null }));
                setSearchContractingParties(false);
                setFilterNames((prev: IObject) => ({
                  ...prev,
                  'Contracting Parties': null,
                }));
              }}
              placeholder="Search party name"
              value={partySearchValue}
              // {...(!partyMasterData?.length && {
              // value:partySearchValue
              // })}
              isLoading={isLoading}
              handleChange={(e: React.BaseSyntheticEvent<HTMLInputElement>) => {
                const val = e.target.value;
                setPartySearchValue(val);
                handleFilterChange(
                  'contracting_parties',
                  val,
                  'Contracting Parties'
                );
              }}
              // renderCustomInput={
              //   partyMasterData?.length
              //     ? () => (
              //         <TableSearchSelectInput
              //           key={
              //             isArray(filters?.contracting_parties)
              //               ? (filters?.contracting_parties?.[0] as string)
              //               : 'contracting_parties'
              //           }
              //           options={sortListAlphabetical(partyMasterData)}
              //           label="Select Parties"
              //           valueKey="name"
              //           value={
              //             filters?.contracting_parties
              //               ? filters.contracting_parties
              //               : []
              //           }
              //           onChange={(value: string[]) => {
              //             if (value?.length) {
              //               handleFilterChange(
              //                 'contracting_parties',
              //                 value,
              //                 'Party Master'
              //               );
              //             } else {
              //               setFilters((prev: IObject) => ({
              //                 ...prev,
              //                 contracting_parties: null,
              //               }));
              //               setFilterNames((prev: IObject) => ({
              //                 ...prev,
              //                 'Party Masters': null,
              //               }));
              //               setPageNumberChange(0);
              //             }
              //           }}
              //           isLoading={isLoading}
              //         />
              //       )
              //     : undefined
              // }
            />
          ) : (
            <TableHeaderWithSearch
              title="Parties"
              setIsSearch={setSearchContractingParties}
              isLoading={isLoading}
            />
          );
        },
        renderCell: (params: any) => {
          const { progress, isProcessing, notStarted, failed } =
            getProgressStatus(params?.row, contractStatus);
          const findPartyInParties = (id: any) => {
            const pid = id;
            return (params?.row?.parties || []).find(
              (party: any) => party?.id === pid
            );
          };
          const copyEdited =
            params?.row?.contracting_parties?.[0]?.copy_edited_content
              ?.contracting_parties;
          const partiesToShow =
            Array.isArray(copyEdited) && copyEdited.length
              ? copyEdited?.map((cp: any) => {
                  if (!cp?.id) return cp;
                  const matched = findPartyInParties(cp?.id);
                  return matched
                    ? {
                        party: matched?.name ? matched?.name : cp?.party,
                      }
                    : cp;
                })
              : params?.row?.parties?.length > 0
                ? params.row.parties
                : params?.row?.extraction_type === 'ai' &&
                    params?.row?.contracting_parties?.[0]?.raw_content?.party
                      ?.data
                  ? params?.row.contracting_parties?.[0]?.raw_content?.party
                      ?.data
                  : params?.row?.contracting_parties?.length > 0
                    ? params.row.contracting_parties
                    : (progressStatus(params?.row?.id)?.contracting_parties ??
                      []);

          return (
            <Tooltip
              title={getTooltipTitle({
                isProcessing,
                notStarted,
                failed,
                progress,
              })}
              arrow
            >
              <Stack sx={{ minWidth: 200 }}>
                <Stack
                  sx={{
                    ...cellStyle({ notStarted, isProcessing }),
                  }}
                >
                  {partiesToShow?.length > 0 ? (
                    <ContractingPartiesList
                      list={partiesToShow}
                      id={`parties-${params?.row?.id}`}
                      title="Contracting parties"
                      filters={filters}
                    />
                  ) : (
                    <Typography sx={{ width: '100%', display: 'block' }}>
                      &nbsp;
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </Tooltip>
          );
        },
      }),
    },
    {
      ...(contractColumns.includes('contract_type') && {
        field: 'contract_type',
        headerName: 'Contract Name',
        minWidth: 290,
        flex: 1,
        sortable: false,
        renderCell: (params: any) => {
          const { progress, isProcessing, notStarted, failed } =
            getProgressStatus(params?.row, contractStatus);
          return (
            <Tooltip
              title={getTooltipTitle({
                isProcessing,
                notStarted,
                failed,
                progress,
              })}
              arrow
            >
              <Stack
                sx={{
                  ...cellStyle({ notStarted, isProcessing }),
                  minWidth: 240,
                }}
                height="-webkit-fill-available"
              >
                {params?.row?.contract_type?.length > 0 ? (
                  <TableChipList
                    id={`contract-${params?.row?.id}`}
                    labelKey="displayName"
                    valueKey="id"
                    list={params?.row?.contract_type || []}
                    chipColor="#C4DBFF"
                    title="Contracts Name"
                    onClick={(contractType: any) =>
                      handleFilterClick(
                        'contracttype',
                        'Contract Name',
                        contractType,
                        setSearchContractType
                      )
                    }
                  />
                ) : (
                  <Typography sx={{ width: '100%', display: 'block' }}>
                    &nbsp;
                  </Typography>
                )}
              </Stack>
            </Tooltip>
          );
        },
        renderHeader: () => {
          return searchContractType ? (
            <TableSearchInputComponent
              key="contract_type"
              setIsSearch={() => {
                setFilters((prev) => ({ ...prev, contracttype: null }));
                setSearchContractType(false);
                setFilterNames((prev: IObject) => ({
                  ...prev,
                  'Contract Name': null,
                }));
              }}
              placeholder="Search contract name"
              selectedValue={
                filters?.contracttype ? filters.contracttype : null
              }
              isLoading={isLoading}
              renderCustomInput={() => (
                <TableSearchSelectInput
                  key={
                    isArray(filters?.contracttype)
                      ? (filters?.contracttype?.[0] as string)
                      : 'contracttype'
                  }
                  label="Select contract name"
                  labelKey="displayName"
                  options={sortListAlphabetical(contracts, 'displayName')}
                  valueKey="id"
                  value={filters?.contracttype ? filters.contracttype : null}
                  onChange={(value: string[]) => {
                    if (value?.length > 0) {
                      handleFilterChange(
                        'contracttype',
                        value,
                        'Contract Name'
                      );
                    } else {
                      setFilters((prev: IObject) => ({
                        ...prev,
                        contracttype: null,
                      }));
                      setFilterNames((prev: IObject) => ({
                        ...prev,
                        'Contract Name': null,
                      }));
                      setPageNumberChange(0);
                    }
                  }}
                  isLoading={isLoading}
                />
              )}
            />
          ) : (
            <TableHeaderWithSearch
              title="Contract Name"
              setIsSearch={setSearchContractType}
              isLoading={isLoading}
            />
          );
        },
      }),
    },
    {
      ...(contractColumns.includes('contract_category') &&
        isShowCategoryColumn && {
          field: 'contract_category',
          headerName: 'Contract Category',
          minWidth: 290,
          flex: 1,
          sortable: false,
          renderCell: (params: any) => {
            const { progress, isProcessing, notStarted, failed } =
              getProgressStatus(params?.row, contractStatus);

            const selectedFilters: any = filters?.contract_category || [];
            let reOrderCategories = params?.row?.contract_category || [];
            if (!Array.isArray(reOrderCategories)) {
              reOrderCategories = reOrderCategories ? [reOrderCategories] : [];
            }
            if (selectedFilters?.length) {
              const filterSelected = reOrderCategories?.filter((item: any) =>
                selectedFilters?.includes(item?.name)
              );
              const filterNotSelected = reOrderCategories?.filter(
                (item: any) => !selectedFilters?.includes(item?.name)
              );
              reOrderCategories = [...filterSelected, ...filterNotSelected];
            }
            return (
              <Tooltip
                title={getTooltipTitle({
                  isProcessing,
                  notStarted,
                  failed,
                  progress,
                })}
                arrow
              >
                <Stack
                  sx={{
                    ...cellStyle({ notStarted, isProcessing }),
                    minWidth: 240,
                  }}
                  height="-webkit-fill-available"
                  justifyContent="center"
                >
                  <TableChipList
                    id={`category-${params?.row?.id}`}
                    chipColor="#C4DBFF"
                    valueKey="id"
                    list={reOrderCategories}
                    onClick={() =>
                      handleFilterClick(
                        'contractcategory',
                        'Category',
                        params?.row?.contract_category,
                        setSearchCategory
                      )
                    }
                  />
                </Stack>
              </Tooltip>
            );
          },
          renderHeader: () => {
            return searchCategory ? (
              <TableSearchInputComponent
                key="contract_category"
                setIsSearch={() => {
                  setFilters((prev) => ({
                    ...prev,
                    contractcategory: null,
                  }));
                  setSearchCategory(false);
                  setFilterNames((prev: IObject) => ({
                    ...prev,
                    'Contract Category': null,
                  }));
                }}
                placeholder="Search contract category"
                selectedValue={
                  filters?.contractcategory ? filters.contractcategory : null
                }
                isLoading={isLoading}
                renderCustomInput={() => (
                  <TableSearchSelectInput
                    key={
                      isArray(filters?.contractcategory)
                        ? (filters?.contractcategory?.[0] as string)
                        : 'contract_category'
                    }
                    label="Select contract category"
                    options={sortListAlphabetical(contractCategory)}
                    labelKey="name"
                    valueKey="id"
                    value={
                      filters?.contractcategory
                        ? filters.contractcategory
                        : null
                    }
                    onChange={(value: string[]) => {
                      if (value.length > 0) {
                        handleFilterChange(
                          'contractcategory',
                          value,
                          'Contract Category'
                        );
                      } else {
                        setFilters((prev: IObject) => ({
                          ...prev,
                          contractcategory: null,
                        }));
                        setFilterNames((prev: IObject) => ({
                          ...prev,
                          'Contract Category': null,
                        }));
                        setPageNumberChange(0);
                      }
                    }}
                    isLoading={isLoading}
                  />
                )}
              />
            ) : (
              <TableHeaderWithSearch
                title="Contract Category"
                width="140px"
                setIsSearch={setSearchCategory}
                isLoading={isLoading}
              />
            );
          },
        }),
    },
    {
      ...(contractColumns.includes('product_masters') && {
        field: 'product_masters',
        headerName: 'Products',
        minWidth: 290,
        flex: 1,
        sortable: false,
        renderCell: (params: any) => {
          const selectedFilters: any = filters?.productmasters || [];
          let reOrderProductMasters = params?.row?.product_masters || [];
          if (selectedFilters?.length) {
            const filterSelectedGroups = reOrderProductMasters?.filter(
              (item: any) => selectedFilters?.includes(item?.id)
            );
            const filterNotSelectedGroups = reOrderProductMasters?.filter(
              (item: any) => !selectedFilters?.includes(item?.id)
            );
            reOrderProductMasters = [
              ...filterSelectedGroups,
              ...filterNotSelectedGroups,
            ];
          }
          const { progress, isProcessing, notStarted, failed } =
            getProgressStatus(params?.row, contractStatus);
          return (
            <Tooltip
              title={getTooltipTitle({
                isProcessing,
                notStarted,
                failed,
                progress,
              })}
              arrow
            >
              <Stack
                sx={{
                  ...cellStyle({ notStarted, isProcessing }),
                  minWidth: 240,
                }}
                height="-webkit-fill-available"
              >
                {reOrderProductMasters?.length > 0 ? (
                  <TableChipList
                    id={`product-master-${params?.row?.id}`}
                    labelKey="name"
                    valueKey="id"
                    list={reOrderProductMasters}
                    chipColor="#DCBDE7"
                    onClick={(product: any) =>
                      handleFilterClick(
                        'productmasters',
                        'Product Master',
                        product,
                        setSearchProductMaster
                      )
                    }
                  />
                ) : (
                  <Typography sx={{ width: '100%', display: 'block' }}>
                    &nbsp;
                  </Typography>
                )}
              </Stack>
            </Tooltip>
          );
        },
        renderHeader: () => {
          return searchProductMaster ? (
            <TableSearchInputComponent
              key="product_masters"
              setIsSearch={() => {
                setFilters((prev) => ({ ...prev, productmasters: null }));
                setSearchProductMaster(false);
                setFilterNames((prev: IObject) => ({
                  ...prev,
                  'Product Masters': null,
                }));
              }}
              isLoading={isLoading}
              placeholder="Search Products"
              renderCustomInput={() => (
                <TableSearchSelectInput
                  key={
                    isArray(filters?.productmasters)
                      ? (filters?.productmasters?.[0] as string)
                      : 'product_masters'
                  }
                  options={sortListAlphabetical(productMasterData)}
                  label="Select Products"
                  valueKey="id"
                  value={filters?.productmasters ? filters.productmasters : []}
                  onChange={(value: string[]) => {
                    if (value?.length) {
                      handleFilterChange(
                        'productmasters',
                        value,
                        'Product Master'
                      );
                    } else {
                      setFilters((prev: IObject) => ({
                        ...prev,
                        productmasters: null,
                      }));
                      setFilterNames((prev: IObject) => ({
                        ...prev,
                        'Product Masters': null,
                      }));
                      setPageNumberChange(0);
                    }
                  }}
                  isLoading={isLoading}
                  groupedOptions={true}
                />
              )}
            />
          ) : (
            <TableHeaderWithSearch
              title="Products"
              isLoading={isLoading}
              setIsSearch={setSearchProductMaster}
            />
          );
        },
      }),
    },
    {
      ...(contractColumns.includes('groups') && {
        field: 'groups',
        headerName: 'Group',
        minWidth: 290,
        flex: 1,
        sortable: false,
        renderCell: (params: any) => {
          const selectedFilters: any = filters?.groups || [];
          let reOrderGroups =
            params?.row?.business_spoc?.[0]?.groups ??
            params?.row?.creator_details?.groups ??
            [];
          if (selectedFilters?.length) {
            const filterSelectedGroups = reOrderGroups?.filter((item: any) =>
              selectedFilters?.includes(item?.name)
            );
            const filterNotSelectedGroups = reOrderGroups?.filter(
              (item: any) => !selectedFilters?.includes(item?.name)
            );
            reOrderGroups = [
              ...filterSelectedGroups,
              ...filterNotSelectedGroups,
            ];
          }
          const { progress, isProcessing, notStarted, failed } =
            getProgressStatus(params?.row, contractStatus);
          return (
            <Tooltip
              title={getTooltipTitle({
                isProcessing,
                notStarted,
                failed,
                progress,
              })}
              arrow
            >
              <Stack
                sx={{
                  ...cellStyle({ notStarted, isProcessing }),
                  minWidth: 240,
                }}
                height="-webkit-fill-available"
              >
                {reOrderGroups?.length > 0 ? (
                  <TableChipList
                    id={`groups-${params.row.id}`}
                    list={reOrderGroups}
                    labelKey=""
                    valueKey=""
                    chipColor="#DCBDE7"
                    onClick={(groups: any) =>
                      handleFilterClick(
                        'groups',
                        'Group',
                        groups,
                        setSearchGroups
                      )
                    }
                  />
                ) : (
                  <Typography sx={{ width: '100%', display: 'block' }}>
                    &nbsp;
                  </Typography>
                )}
              </Stack>
            </Tooltip>
          );
        },
        renderHeader: () => {
          return searchGroups ? (
            <TableSearchInputComponent
              key="groups"
              setIsSearch={() => {
                setFilters((prev) => ({ ...prev, groups: null }));
                setSearchGroups(false);
                setFilterNames((prev: IObject) => ({
                  ...prev,
                  Groups: null,
                }));
              }}
              isLoading={isLoading}
              placeholder="Search Groups"
              renderCustomInput={() => (
                <TableSearchSelectInput
                  key={
                    isArray(filters?.groups)
                      ? (filters?.groups?.[0] as string)
                      : 'groups'
                  }
                  options={sortListAlphabetical(groupData)}
                  label="Select Groups"
                  valueKey="name"
                  value={filters?.groups ? filters.groups : []}
                  onChange={(value: string[]) => {
                    if (value?.length) {
                      handleFilterChange('groups', value, 'Groups');
                    }
                  }}
                  isLoading={isLoading}
                />
              )}
            />
          ) : (
            <TableHeaderWithSearch
              width="80px"
              title="Groups"
              isLoading={isLoading}
              setIsSearch={setSearchGroups}
            />
          );
        },
      }),
    },
    {
      ...(contractColumns.includes('projects') && {
        field: 'projects',
        headerName: projectLabel,
        minWidth: 290,
        flex: 1,
        sortable: false,
        renderCell: (params: any) => {
          const selectedFilters: any = filters?.project || [];
          let reOrderProjects = params?.row?.projects || [];
          if (selectedFilters?.length) {
            const filterSelectedGroups = reOrderProjects?.filter((item: any) =>
              selectedFilters?.includes(item?.id)
            );
            const filterNotSelectedGroups = reOrderProjects?.filter(
              (item: any) => !selectedFilters?.includes(item?.id)
            );
            reOrderProjects = [
              ...filterSelectedGroups,
              ...filterNotSelectedGroups,
            ];
          }
          const cleanedProjectList = reOrderProjects.map((proj: any) => ({
            ...proj,
            name: proj?.parent?.name || proj?.name,
            id: proj?.parent?.id || proj?.id,
          }));
          const { progress, isProcessing, notStarted, failed } =
            getProgressStatus(params?.row, contractStatus);
          return (
            <Tooltip
              title={getTooltipTitle({
                isProcessing,
                notStarted,
                failed,
                progress,
              })}
              arrow
            >
              <Stack
                sx={{
                  ...cellStyle({ notStarted, isProcessing }),
                  minWidth: 240,
                }}
                height="-webkit-fill-available"
              >
                {cleanedProjectList?.length > 0 ? (
                  <TableChipList
                    id={`projects-${params?.row?.id}`}
                    labelKey="name"
                    valueKey="id"
                    list={cleanedProjectList}
                    chipColor="#CDE7BD"
                    title="Projects name"
                    onClick={(projects: any) =>
                      handleFilterClick(
                        'project',
                        'Project',
                        projects,
                        setSearchProjects
                      )
                    }
                  />
                ) : (
                  <Typography sx={{ width: '100%', display: 'block' }}>
                    &nbsp;
                  </Typography>
                )}
              </Stack>
            </Tooltip>
          );
        },
        renderHeader: () => {
          return searchProjects ? (
            <TableSearchInputComponent
              key="projects"
              isLoading={isLoading}
              setIsSearch={() => {
                setFilters((prev) => ({ ...prev, project: null }));
                setSearchProjects(false);
                setFilterNames((prev: IObject) => ({
                  ...prev,
                  Project: null,
                }));
              }}
              placeholder={`Search ${projectLabel}`}
              renderCustomInput={() => (
                <TableSearchSelectInput
                  key={
                    isArray(filters?.project)
                      ? (filters?.project?.[0] as string)
                      : 'projects'
                  }
                  options={sortListAlphabetical(projectData)}
                  label={`Select ${projectLabel}`}
                  valueKey="id"
                  value={filters?.project ? filters?.project : []}
                  onChange={(value: string[]) => {
                    if (value?.length) {
                      handleFilterChange('project', value, 'Project');
                    } else {
                      setFilters((prev: IObject) => ({
                        ...prev,
                        project: null,
                      }));
                      setFilterNames((prev: IObject) => ({
                        ...prev,
                        Project: null,
                      }));
                      setPageNumberChange(0);
                    }
                  }}
                  isLoading={isLoading}
                  groupedOptions={true}
                />
              )}
            />
          ) : (
            <TableHeaderWithSearch
              width="80px"
              title={projectLabel}
              isLoading={isLoading}
              setIsSearch={setSearchProjects}
            />
          );
        },
      }),
    },
    {
      ...(contractColumns.includes('territory') && {
        field: 'territory',
        headerName: territory?.name,
        minWidth: 300,
        flex: 1,
        sortable: false,
        renderCell: (params: any) => {
          const { progress, isProcessing, notStarted, failed } =
            getProgressStatus(params?.row, contractStatus);
          const selectedFilters: any = filters?.territory || [];
          let reorderTerritory = params?.row?.territory || [];
          if (selectedFilters?.length) {
            const filterSelectedTerritory = reorderTerritory?.filter(
              (item: any) => selectedFilters?.includes(item?.id)
            );
            const filterNotSelectedTerritory = reorderTerritory?.filter(
              (item: any) => !selectedFilters?.includes(item?.id)
            );
            reorderTerritory = [
              ...filterSelectedTerritory,
              ...filterNotSelectedTerritory,
            ];
          }
          return (
            <Tooltip
              title={getTooltipTitle({
                isProcessing,
                notStarted,
                failed,
                progress,
              })}
              arrow
            >
              <Stack
                sx={{
                  ...cellStyle({ notStarted, isProcessing }),
                  minWidth: 260,
                }}
                height="-webkit-fill-available"
              >
                {reorderTerritory.length > 0 ? (
                  <TableChipList
                    id={`-${params?.row?.id}`}
                    labelKey="name"
                    valueKey="id"
                    list={reorderTerritory}
                    chipColor="#DCBDE7"
                    onClick={(territory: any) =>
                      handleFilterClick(
                        'territory',
                        'Territory',
                        territory,
                        setSearchTerritory
                      )
                    }
                  />
                ) : (
                  <Typography sx={{ width: '100%', display: 'block' }}>
                    &nbsp;
                  </Typography>
                )}
              </Stack>
            </Tooltip>
          );
        },
        renderHeader: () => {
          return searchTerritory ? (
            <TableSearchInputComponent
              key="territory"
              setIsSearch={() => {
                setFilters((prev) => ({ ...prev, territory: null }));
                setSearchTerritory(false);
                setFilterNames((prev: IObject) => ({
                  ...prev,
                  Territory: null,
                }));
              }}
              isLoading={isLoading}
              placeholder={`Search ${territory?.name}`}
              renderCustomInput={() => (
                <TableSearchSelectInput
                  key={
                    isArray(filters?.territory)
                      ? (filters?.territory?.[0] as string)
                      : 'territory'
                  }
                  options={sortListAlphabetical(territoryMasterData)}
                  label={`Select ${territory?.name}`}
                  valueKey="id"
                  value={filters?.territory ? filters?.territory : []}
                  onChange={(value: string[]) => {
                    if (value?.length) {
                      handleFilterChange('territory', value, 'Territory');
                    } else {
                      setFilters((prev: IObject) => ({
                        ...prev,
                        territory: null,
                      }));
                      setFilterNames((prev: IObject) => ({
                        ...prev,
                        Territory: null,
                      }));
                      setPageNumberChange(0);
                    }
                  }}
                  isLoading={isLoading}
                  groupedOptions={true}
                />
              )}
            />
          ) : (
            <TableHeaderWithSearch
              title={territory?.name}
              isLoading={isLoading}
              setIsSearch={setSearchTerritory}
            />
          );
        },
      }),
    },
    {
      ...(contractColumns.includes('source_type') && {
        field: 'source_type',
        headerName: 'Source',
        minWidth: 300,
        flex: 1,
        sortable: false,
        renderCell: (params: any) => {
          if (!params?.row?.source_type) return null;
          const { progress, isProcessing, notStarted, failed } =
            getProgressStatus(params.row, contractStatus);
          return (
            <Tooltip
              title={getTooltipTitle({
                isProcessing,
                notStarted,
                failed,
                progress,
              })}
              arrow
            >
              <Stack
                sx={{
                  ...cellStyle({ notStarted, isProcessing }),
                  minWidth: 260,
                  justifyContent: 'center',
                }}
                height="-webkit-fill-available"
              >
                <Chip
                  sx={{
                    background: '#C4DBFF',
                    color: 'riSecondary.900',
                    width: 'fit-content',
                  }}
                  label={
                    <OverflowTip
                      value={sourceTypes?.[params?.row?.source_type]}
                    />
                  }
                  onClick={() =>
                    handleFilterClick(
                      'source_type',
                      'Source',
                      params?.row?.source_type,
                      setSearchSource
                    )
                  }
                />
              </Stack>
            </Tooltip>
          );
        },
        renderHeader: () => {
          return searchSource ? (
            <TableSearchInputComponent
              key="source"
              setIsSearch={() => {
                setFilters((prev) => ({
                  ...prev,
                  source_type: null,
                }));
                setSearchSource(false);
                setFilterNames((prev: IObject) => ({
                  ...prev,
                  Source: null,
                }));
              }}
              placeholder="Search source"
              isLoading={isLoading}
              selectedValue={filters?.source_type ? filters.source_type : []}
              renderCustomInput={() => (
                <TableSearchSelectInput
                  key={
                    isArray(filters?.source_type)
                      ? (filters?.source_type?.[0] as string)
                      : 'source_type'
                  }
                  label="Select Source"
                  options={sortListAlphabetical(sourceTypesArray)}
                  labelKey="name"
                  valueKey="id"
                  value={filters?.source_type ? filters.source_type : []}
                  isLoading={isLoading}
                  onChange={(value: string[]) => {
                    if (value.length > 0) {
                      handleFilterChange('source_type', value, 'Source');
                    } else {
                      setFilters((prev: IObject) => ({
                        ...prev,
                        source_type: null,
                      }));
                      setFilterNames((prev: IObject) => ({
                        ...prev,
                        Source: null,
                      }));
                      setPageNumberChange(0);
                    }
                  }}
                />
              )}
            />
          ) : (
            <TableHeaderWithSearch
              title="Source"
              width="140px"
              setIsSearch={setSearchSource}
              isLoading={isLoading}
            />
          );
        },
      }),
    },
    {
      ...(contractColumns.includes('owner') && {
        field: 'creator',
        headerName: 'Owner',
        minWidth: searchOwner ? 300 : 200,
        flex: 1,
        sortable: false,
        renderCell: (params: any) => {
          const owner = params?.row?.owners;
          return (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
              height="100%"
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                className="custom-horizontal-scroll"
                maxWidth="11rem"
              >
                {owner?.map((item: any) => (
                  <NameAvatar
                    key={item?.id}
                    firstName={item?.first_name}
                    lastName={item?.last_name}
                    avatarStyle={{
                      background: !item?.is_active
                        ? 'rgba(0, 0, 0, 0.25)'
                        : '#A0597F',
                    }}
                  />
                ))}
              </Stack>
            </Stack>
          );
        },
        renderHeader: () => {
          return searchOwner ? (
            <TableSearchInputComponent
              key="creator"
              setIsSearch={() => {
                setFilters((prev) => ({ ...prev, creator: null }));
                setSearchOwner(false);
              }}
              placeholder="Search Owner"
              renderCustomInput={() => (
                <TableSearchSelectInput
                  key="creator"
                  label="Select Owner"
                  options={sortListAlphabetical(ownerData)}
                  valueKey="id"
                  value={filters?.creator ? filters.creator : []}
                  onChange={(value: string[]) => {
                    if (value.length) {
                      handleFilterChange('creator', value, 'Owner');
                    }
                  }}
                  isLoading={isLoading}
                />
              )}
            />
          ) : (
            <TableHeaderWithSearch title="Owner" setIsSearch={setSearchOwner} />
          );
        },
      }),
    },
    {
      ...(contractColumns.includes('agreement_date') && {
        field: 'agreement_date',
        headerName: 'Date Of Agreement',
        minWidth: 180,
        flex: 1,
        renderCell: (params: any) => {
          const { progress, isProcessing, notStarted, failed } =
            getProgressStatus(params?.row, contractStatus);
          const matchedStatus = Array.isArray(contractStatus)
            ? contractStatus?.find((c: any) => c.id === params?.row?.id)
            : null;
          const agreementDate =
            params?.row?.agreement_date || matchedStatus?.agreement_date;
          const formattedDate = agreementDate
            ? dayjs(agreementDate).format('DD-MM-YYYY')
            : '_';

          return (
            <Tooltip
              title={getTooltipTitle({
                isProcessing,
                notStarted,
                failed,
                progress,
              })}
              arrow
            >
              <Stack
                sx={{ minWidth: 120 }}
                height="-webkit-fill-available"
                justifyContent="center"
              >
                <Typography
                  sx={{
                    ...cellStyle({ notStarted, isProcessing }),
                    cursor: 'text',
                  }}
                >
                  {formattedDate}
                </Typography>
              </Stack>
            </Tooltip>
          );
        },
      }),
    },
    {
      ...(contractColumns.includes('start_date') && {
        field: 'start_date',
        headerName: 'Start Date',
        minWidth: 120,
        flex: 1,
        renderCell: (params: any) => {
          const { progress, isProcessing, notStarted, failed } =
            getProgressStatus(params?.row, contractStatus);
          const matchedStatus = Array.isArray(contractStatus)
            ? contractStatus?.find((c: any) => c.id === params?.row?.id)
            : null;
          const startDate =
            params?.row?.start_date || matchedStatus?.start_date;
          const formattedDate = startDate
            ? dayjs(startDate).format('DD-MM-YYYY')
            : '_';

          return (
            <Tooltip
              title={getTooltipTitle({
                isProcessing,
                notStarted,
                failed,
                progress,
              })}
              arrow
            >
              <Stack
                sx={{ minWidth: 120 }}
                height="-webkit-fill-available"
                justifyContent="center"
              >
                <Typography
                  sx={{
                    ...cellStyle({ notStarted, isProcessing }),
                    cursor: 'text',
                  }}
                >
                  {formattedDate}
                </Typography>
              </Stack>
            </Tooltip>
          );
        },
      }),
    },
    {
      ...(contractColumns.includes('end_date') && {
        field: 'end_date',
        headerName: 'Expires on',
        minWidth: 120,
        flex: 1,
        renderCell: (params: any) => {
          const { progress, isProcessing, notStarted, failed } =
            getProgressStatus(params?.row, contractStatus);
          const matchedStatus = Array.isArray(contractStatus)
            ? contractStatus?.find((c: any) => c?.id === params?.row?.id)
            : null;
          const endDate = params?.row?.end_date || matchedStatus?.end_date;
          const formattedEndDate = endDate
            ? dayjs(endDate).format('DD-MM-YYYY')
            : '_';

          return (
            <Tooltip
              title={getTooltipTitle({
                isProcessing,
                notStarted,
                failed,
                progress,
              })}
              arrow
            >
              <Stack
                sx={{ minWidth: 120 }}
                height="-webkit-fill-available"
                justifyContent="center"
              >
                <Typography
                  sx={{
                    ...cellStyle({ notStarted, isProcessing }),
                    cursor: 'text',
                  }}
                >
                  {formattedEndDate}
                </Typography>
              </Stack>
            </Tooltip>
          );
        },
      }),
    },
    // {
    //   field: 'status',
    //   headerName: 'Status',
    //   minWidth: 225,
    //   flex: 1,
    //   sortable: false,
    //   renderCell: (params: any) => {
    //     return (
    //       <>
    //         {(params?.row?.status === 'Done' ||
    //           progressStatus(params?.row?.id)?.status === 'Done') && (
    //           <CheckCircleOutlineIcon style={{ color: 'green' }} />
    //         )}
    //         {progressStatus(params?.row?.id)?.status === 'Processing' &&
    //         getFailedStatus(params?.row?.created_on) ? (
    //           <CircularProgressWithLabel
    //             color="success"
    //             value={
    //               parseInt(progressStatus(params?.row?.id)?.percentage) || 0
    //             }
    //           />
    //         ) : (
    //           params?.row?.status === 'Not Started' &&
    //           progressStatus(params?.row?.id)?.status !== 'Done' && (
    //             <CircularProgressWithLabel color="success" value={0} />
    //           )
    //         )}
    //         {(params?.row?.status === 'Failed' ||
    //           progressStatus(params?.row?.id)?.status === 'Failed' ||
    //           (!getFailedStatus(params?.row?.created_on) &&
    //             progressStatus(params?.row?.id)?.status === 'Processing' &&
    //             parseInt(progressStatus(params?.row?.id)?.percentage) ===
    //               0)) && <CancelOutlinedIcon style={{ color: 'red' }} />}
    //       </>
    //     );
    //   },
    //   renderHeader: () => {
    //     return searchStatus ? (
    //       <TableSearchInputComponent
    //         key="status"
    //         setIsSearch={() => {
    //           setFilters((prev) => ({ ...prev, status: null }));
    //           setSearchStatus(false);
    //         }}
    //         placeholder="Search Status"
    //         renderCustomInput={() => (
    //           <TableAsyncSelectInput
    //             key={
    //               isArray(filters?.status)
    //                 ? (filters?.status?.[0] as string)
    //                 : 'status'
    //             }
    //             options={statusFilterList}
    //             label="Select Status"
    //             valueKey="value"
    //             value={filters?.status ? filters?.status : []}
    //             onChange={(value: string[]) => {
    //               if (value?.length) {
    //                 handleFilterChange('status', value, 'Status);
    //               }
    //             }}
    //           />
    //         )}
    //       />
    //     ) : (
    //       <TableHeaderWithSearch
    //         width="80px"
    //         title="Status"
    //         setIsSearch={setSearchStatus}
    //       />
    //     );
    //   },
    // },
  ];

  const groupingColDef: DataGridProProps['groupingColDef'] = {
    headerName: 'Title',
    minWidth: 310,
    cellClassName: 'group-cell',
    headerClassName: 'group-header',
    renderHeader: () => {
      return searchContract ? (
        <TableSearchInputComponent
          key="contract"
          setIsSearch={() => {
            setFilters((prev) => ({ ...prev, title: null }));
            setSearchContract(false);
          }}
          placeholder="Search Name"
          handleChange={(e: React.BaseSyntheticEvent<HTMLInputElement>) => {
            if (e.target.value?.length) {
              handleFilterChange('title', e.target.value, 'Title');
            }
          }}
        />
      ) : (
        <Stack onClick={(e: any) => e.stopPropagation()}>
          <TableHeaderWithSearch
            title="Title"
            setIsSearch={setSearchContract}
          />
        </Stack>
      );
    },
    valueFormatter: (value: any, row) => {
      const size = row?.path?.length || 0;
      return (
        <CustomGridTreeDataGroupingCell
          id={value}
          row={row}
          minWidth={
            size ? `calc(240px - ${(size + (size % 2)) * 10 - 6}px)` : '240px'
          }
          {...getProgressStatus(row, contractStatus)}
        />
      );
    },
  };

  // Disable rows when deleted_status is true in tree view
  const isRowSelectable = useCallback(
    (params: any) => {
      if (treeView && params.row?.deleted_status === true) {
        return false;
      }
      return true;
    },
    [treeView]
  );

  // Add disabled class to rows when deleted_status is true in tree view
  const getRowClassName = useCallback(
    (params: any) => {
      if (treeView && params.row?.deleted_status === true) {
        return 'disabled-row';
      }
      return '';
    },
    [treeView]
  );

  // Function to get tree data path for each row
  const getTreeDataPath = useCallback((row: any) => {
    return row.path || [row.id];
  }, []);

  return (
    <DataGridProTable
      {...(treeView
        ? {
            treeData: true,
            getTreeDataPath: getTreeDataPath,
            groupingColDef: groupingColDef,
            rowSelectionPropagation: {
              parents: false,
              descendants: false,
            },
          }
        : {})}
      isRowSelectable={treeView ? isRowSelectable : undefined}
      getRowClassName={treeView ? getRowClassName : undefined}
      dataSource={isLoading ? undefined : customDataSource}
      rows={contractData?.results || []}
      columns={columns.filter((value) => Object.keys(value)?.length !== 0)}
      rowCount={contractData?.count}
      isLoading={isLoading}
      setSelectedRow={setSelectedRow}
      pageNumber={pageNumber}
      setPageNumberChange={setPageNumberChange}
      setSorting={setSorting}
      checkboxSelection
      pinnedColumns={{
        left:
          isMobile || isTablet || folderView
            ? ['__check__']
            : ['__check__', treeView ? GRID_TREE_DATA_GROUPING_FIELD : 'title'],
      }}
      sx={{
        '& .MuiDataGrid-row.disabled-row': {
          opacity: 0.5,
          pointerEvents: 'none',
          cursor: 'not-allowed',
        },
        '& .MuiDataGrid-row.disabled-row .MuiCheckbox-root': {
          opacity: 0.5,
          pointerEvents: 'none',
        },
        '& .MuiDataGrid-row.disabled-row:hover': {
          backgroundColor: 'transparent',
        },
      }}
    />
  );
};

export default DocumentLibraryTable;
